import { type ReactNode } from 'react'
import { logout, useMe } from '../api'
import { AgentPinSection } from '../components/admin/AgentPinSection'
import {
  useSidebarPrefs,
  type SidebarPrefs,
  type SidebarStyle,
  type DesktopPosition,
  type MobilePosition,
} from '../lib/sidebarPrefs'

const styleOptions: { value: SidebarStyle; label: string; description: string }[] = [
  { value: 'classic', label: 'Classic', description: 'Sidebar слева на десктопе, bottom-bar на мобиле' },
  { value: 'dock', label: 'Dock', description: 'Анимированный dock — позицию выбираешь сам' },
]

const desktopOptions: { value: DesktopPosition; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
]

const mobileOptions: { value: MobilePosition; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
]

interface SegmentedProps<T extends string> {
  value: T
  options: { value: T; label: string }[]
  onChange: (next: T) => void
  disabled?: boolean
  ariaLabel: string
}

function Segmented<T extends string>({ value, options, onChange, disabled, ariaLabel }: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex flex-wrap gap-1 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-1 ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
            value === opt.value
              ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

interface SectionProps {
  title: string
  description?: string
  children: ReactNode
}

function Section({ title, description, children }: SectionProps) {
  return (
    <section className="border-b border-gray-200 dark:border-gray-800 py-6 first:pt-0 last:border-b-0">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {description && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
      <div className="mt-3">{children}</div>
    </section>
  )
}

export function SettingsPage() {
  const { data: me } = useMe()
  const { prefs, setPrefs } = useSidebarPrefs(me?.id)

  const update = (patch: Partial<SidebarPrefs>): void => {
    setPrefs({ ...prefs, ...patch })
  }

  const isDock = prefs.style === 'dock'

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Настройки внешнего вида панели — сохраняются в этом браузере для твоей учётки.
        </p>
      </header>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6">
        <Section
          title="Sidebar style"
          description="Classic — фиксированный sidebar; Dock — плавающая панель с magnification."
        >
          <div className="space-y-2">
            {styleOptions.map(opt => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  prefs.style === opt.value
                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30'
                    : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                <input
                  type="radio"
                  name="sidebar-style"
                  value={opt.value}
                  checked={prefs.style === opt.value}
                  onChange={() => update({ style: opt.value })}
                  className="mt-1 accent-blue-600"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>
        </Section>

        {me?.role === 'admin' && (
          <Section
            title="Agent PIN"
            description="Глобальный визуальный замок интерфейса агента."
          >
            <AgentPinSection />
          </Section>
        )}

        <Section title="Desktop position" description="Где dock рендерится на широких экранах (≥768px).">
          <Segmented
            value={prefs.desktopPosition}
            options={desktopOptions}
            onChange={v => update({ desktopPosition: v })}
            disabled={!isDock}
            ariaLabel="Desktop dock position"
          />
        </Section>

        <Section title="Mobile position" description="На мобиле dock всегда горизонтальный — выбери сторону экрана.">
          <Segmented
            value={prefs.mobilePosition}
            options={mobileOptions}
            onChange={v => update({ mobilePosition: v })}
            disabled={!isDock}
            ariaLabel="Mobile dock position"
          />
        </Section>

        {isDock && (
          <Section
            title="Account"
            description="В classic-режиме это видно в самом sidebar; в dock-режиме — здесь."
          >
            <div className="space-y-3 text-sm">
              {me && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">Email</div>
                  <div className="text-gray-900 dark:text-gray-100">{me.email}</div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">Version</div>
                <div className="text-gray-900 dark:text-gray-100 tabular-nums">v{__APP_VERSION__}</div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => logout()}
                  className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
